import React, { useEffect, useState, useRef } from "react";
import api from "@/lib/api";
import { PageHead, Btn, Card, Field, Select, Toggle, ImageUpload, Label, AiCopyButton, AiSeoButton, SeoPreview } from "@/admin/ui";
import { toast } from "sonner";
import { GripVertical } from "lucide-react";

const empty = () => ({ name: "", slug: "", type: "editorial", visible: true, status: "live", sort_order: 0,
  early_access_at: "", go_live_at: "", heading: "", subheading: "", editorial_opening: "", story: "", hero_desktop: "", hero_mobile: "", seo: {} });

const CollectionsAdmin = () => {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const load = () => api.get("/admin/collections").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      if (editing.id) await api.put(`/admin/collections/${editing.id}`, editing);
      else await api.post("/admin/collections", editing);
      toast.success("Saved"); setEditing(null); load();
    } catch { toast.error("Save failed"); }
  };
  const del = async (id) => { if (!window.confirm("Delete collection?")) return; await api.delete(`/admin/collections/${id}`); load(); };

  const up = (k, v) => setEditing((e) => ({ ...e, [k]: v }));

  const dragIdx = useRef(null);
  const onDrop = async (i) => {
    const from = dragIdx.current;
    if (from === null || from === i) return;
    const arr = [...items]; const [m] = arr.splice(from, 1); arr.splice(i, 0, m);
    setItems(arr); dragIdx.current = null;
    try { await api.post("/admin/collections/reorder", { ids: arr.map((x) => x.id) }); toast.success("Order saved"); }
    catch { toast.error("Reorder failed"); }
  };

  if (editing) return (
    <div className="max-w-2xl">
      <PageHead title={editing.id ? "Edit Collection" : "New Collection"}>
        <Btn variant="ghost" onClick={() => setEditing(null)}>Cancel</Btn>
        <Btn onClick={save} testid="admin-save">Save</Btn>
      </PageHead>
      <Card>
        <Field label="Name" value={editing.name} onChange={(v) => up("name", v)} />
        <Field label="Slug" value={editing.slug} onChange={(v) => up("slug", v)} placeholder="auto from name" />
        <Select label="Type" value={editing.type} onChange={(v) => up("type", v)}
          options={[{ value: "new-in", label: "New In (latest live)" }, { value: "women", label: "Women (department)" }, { value: "men", label: "Men (department)" }, { value: "drop", label: "Drop" }, { value: "editorial", label: "Editorial / Collection" }]} />
        <Select label="Status" value={editing.status} onChange={(v) => up("status", v)}
          options={["coming_soon", "early_access", "live", "archived"]} />
        <Toggle label="Visible in navigation" value={editing.visible} onChange={(v) => up("visible", v)} />
        <Field label="Sort order" type="number" value={editing.sort_order} onChange={(v) => up("sort_order", v)} />
        <Field label="Early access opens (date & time)" type="datetime-local" value={editing.early_access_at} onChange={(v) => up("early_access_at", v)} />
        <p className="text-xs text-espresso/50 -mt-2 mb-4">Optional. Pieces become available (early access) at this time — share the collection link with subscribers.</p>
        <Field label="Full go-live (date & time)" type="datetime-local" value={editing.go_live_at} onChange={(v) => up("go_live_at", v)} />
        <p className="text-xs text-espresso/50 -mt-2 mb-4">Optional. When this passes, everything in the collection flips fully live.</p>
        <Field label="Editorial opening" value={editing.editorial_opening} onChange={(v) => up("editorial_opening", v)} />
        <Field label="Heading (H1)" value={editing.heading} onChange={(v) => up("heading", v)} />
        <Field label="Subheading" value={editing.subheading} onChange={(v) => up("subheading", v)} />
        <Field label="Story" textarea value={editing.story} onChange={(v) => up("story", v)} />
        <AiCopyButton instruction={`Write the collection story for "${editing.name || editing.heading || "this collection"}" in the brand voice.`} current={editing.story} onResult={(t) => up("story", t)} testid="collection-ai-story" />
        <ImageUpload label="Hero — Desktop" value={editing.hero_desktop} onChange={(v) => up("hero_desktop", v)} />
        <ImageUpload label="Hero — Mobile" value={editing.hero_mobile} onChange={(v) => up("hero_mobile", v)} />
        <AiSeoButton subject={editing.name || editing.heading} context={editing.story || editing.subheading} onResult={(d) => up("seo", { ...editing.seo, title: d.title, description: d.description })} testid="collection-ai-seo" />
        <Label>SEO Title</Label>
        <Field label="" value={editing.seo?.title} onChange={(v) => up("seo", { ...editing.seo, title: v })} />
        <Field label="SEO Description" textarea value={editing.seo?.description} onChange={(v) => up("seo", { ...editing.seo, description: v })} />
        <SeoPreview title={editing.seo?.title} description={editing.seo?.description} path={`/collections/${editing.slug || ""}`} />
      </Card>
    </div>
  );

  return (
    <div>
      <PageHead title="Collections & Drops" sub={`${items.length} collections`}>
        <Btn onClick={() => setEditing(empty())} testid="admin-new-collection">+ New Collection</Btn>
      </PageHead>
      <p className="text-xs text-espresso/40 mb-3">Drag by the handle to reorder how collections appear in navigation.</p>
      <div className="bg-cream border border-espresso/10">
        {items.map((c, idx) => (
          <div key={c.id} draggable onDragStart={() => (dragIdx.current = idx)} onDragOver={(e) => e.preventDefault()} onDrop={() => onDrop(idx)}
            data-testid={`admin-collection-row-${c.slug}`}
            className="flex items-center gap-4 px-5 py-3 border-b border-espresso/5">
            <GripVertical size={16} className="text-espresso/30 cursor-move shrink-0" />
            <div className="flex-1">
              <p className="font-serif text-lg">{c.name} <span className="text-xs text-espresso/40">/collections/{c.slug}</span></p>
              <p className="text-xs text-espresso/50">{c.type} · {c.status} · {c.visible ? "visible" : "hidden"}</p>
            </div>
            <Btn variant="ghost" onClick={() => setEditing(c)}>Edit</Btn>
            <button onClick={() => del(c.id)} className="text-wine text-xs tracking-widest uppercase">Delete</button>
          </div>
        ))}
      </div>
    </div>
  );
};

export default CollectionsAdmin;
